import { useState, useEffect } from 'react';
import api from '../api';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';

// What an invoice prints for a part or product (the director, 15 Oct 2026):
// the number (the part's own), the name -- its own, or a different one only
// where the customer calls it something else -- the unit and the price.
// Directors only: for anyone else this renders nothing and asks for nothing,
// because prices stay hidden from the floor. Loads and saves itself through
// the price list, so the part's own form never carries a price.

const TEAL = '#14b8a6';
const BORD = '#2a3040';
const MUTED = '#6b7280';
const AMBER = '#f59e0b';
const inp = { background: '#161b22', border: `1px solid ${BORD}`, borderRadius: '8px', padding: '7px 10px', fontSize: '13px', color: '#f0f2f5', outline: 'none', width: '100%', boxSizing: 'border-box', fontFamily: 'system-ui' };
const fmtPrice = (p) => `€ ${Number(p).toFixed(4).replace('.', ',')}`;

export default function InvoiceCard({ kind, id, ownName, number, cardStyle, titleStyle }) {
  const { isDirector } = useAuth();
  const { toast } = useToast();
  const director = isDirector();
  const [entry, setEntry] = useState(null);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!director || !id) return;
    api.get(`/api/v1/prices/${kind}/${id}`).then(r => setEntry(r.data)).catch(() => setEntry(null));
  }, [director, kind, id]);

  if (!director) return null;

  async function save() {
    setSaving(true);
    try {
      const { data } = await api.put(`/api/v1/prices/${kind}/${id}`, {
        invoice_name: form.invoice_name, invoice_unit: form.invoice_unit, invoice_price: form.invoice_price,
      });
      setEntry(data);
      setForm(null);
      toast.success(data.changed ? 'Saved' : 'Nothing changed');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Could not save');
    } finally {
      setSaving(false);
    }
  }

  const row = (label, value, note) => (
    <div style={{ display: 'flex', justifyContent: 'space-between', gap: '12px', padding: '5px 0', fontSize: '13px', borderBottom: `1px solid ${BORD}` }}>
      <span style={{ color: MUTED }}>{label}</span>
      <span style={{ color: '#f0f2f5', textAlign: 'right' }}>{value}{note && <span style={{ display: 'block', fontSize: '11px', color: MUTED }}>{note}</span>}</span>
    </div>
  );

  return (
    <div style={cardStyle}>
      <div style={{ ...titleStyle, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span>On invoices <i className="fa-solid fa-lock" title="Directors only" style={{ fontSize: '9px', marginLeft: '4px', color: MUTED }} /></span>
        {entry && !form && (
          <button onClick={() => setForm({ invoice_name: entry.invoice_name || '', invoice_unit: entry.invoice_unit || 'pcs', invoice_price: entry.invoice_price ?? '' })}
            style={{ background: 'none', border: `1px solid rgba(20,184,166,0.6)`, color: TEAL, borderRadius: '6px', padding: '2px 9px', fontSize: '11px', fontWeight: '700', cursor: 'pointer', textTransform: 'none', letterSpacing: 0 }}>
            Edit
          </button>
        )}
      </div>
      {!entry && <div style={{ fontSize: '12px', color: MUTED }}>Loading…</div>}
      {entry && !form && (
        <>
          {row('Number', number || <span style={{ color: MUTED }}>none set</span>)}
          {row('Name', entry.invoice_name || ownName, entry.invoice_name ? `in the app: ${ownName}` : 'as in the app')}
          {row('Unit', entry.invoice_unit)}
          {row('Price', entry.invoice_price === null || entry.invoice_price === undefined
            ? <span style={{ color: AMBER }}>no price yet</span>
            : `${fmtPrice(entry.invoice_price)} / ${entry.invoice_unit}`)}
        </>
      )}
      {form && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
          <label style={{ fontSize: '11px', color: MUTED }}>Name on the invoice — only if the customer calls it something else
            <input style={{ ...inp, marginTop: '4px' }} value={form.invoice_name} placeholder={ownName}
              onChange={e => setForm(f => ({ ...f, invoice_name: e.target.value }))} />
          </label>
          <div style={{ display: 'flex', gap: '10px' }}>
            <label style={{ fontSize: '11px', color: MUTED, flex: '0 0 90px' }}>Unit
              <select style={{ ...inp, marginTop: '4px' }} value={form.invoice_unit} onChange={e => setForm(f => ({ ...f, invoice_unit: e.target.value }))}>
                <option value="pcs">pcs</option><option value="kg">kg</option>
              </select>
            </label>
            <label style={{ fontSize: '11px', color: MUTED, flex: 1 }}>Price (€, up to 4 decimals)
              <input style={{ ...inp, marginTop: '4px', textAlign: 'right' }} value={form.invoice_price} placeholder="0,2181"
                onChange={e => setForm(f => ({ ...f, invoice_price: e.target.value }))} />
            </label>
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
            <button onClick={() => setForm(null)} style={{ background: 'none', border: '1px solid #374151', color: '#9ca3af', borderRadius: '8px', padding: '6px 12px', fontSize: '12px', cursor: 'pointer' }}>Cancel</button>
            <button onClick={save} disabled={saving} style={{ background: TEAL, border: 'none', color: '#0f172a', borderRadius: '8px', padding: '6px 14px', fontSize: '12px', fontWeight: '700', cursor: 'pointer' }}>{saving ? 'Saving…' : 'Save'}</button>
          </div>
        </div>
      )}
    </div>
  );
}
